import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Folder as FolderIcon, ChevronDown, Plus, Check } from 'lucide-react'
import { useFolders, useCreateFolder } from '../../hooks/useFolders'

interface FolderPickerProps {
  actorId?: string
  selectedFolderId?: number | null
  onSelect: (folderId: number | null) => void
  compact?: boolean
}

export function FolderPicker({ actorId, selectedFolderId, onSelect, compact }: FolderPickerProps) {
  const { t } = useTranslation()
  const { data: folders, isLoading } = useFolders(actorId)
  const createFolder = useCreateFolder()

  const [isOpen, setIsOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isOpen) return
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setIsCreating(false)
        setNewFolderName('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  useEffect(() => {
    if (isCreating && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isCreating])

  const selectedFolder = folders?.find((f) => f.id === selectedFolderId)
  const displayName = selectedFolder?.name ?? t('organize.noFolder')

  const handleCreate = async () => {
    const name = newFolderName.trim()
    if (!name) return
    try {
      const folder = await createFolder.mutateAsync({ name, actorId })
      onSelect(folder.id)
      setNewFolderName('')
      setIsCreating(false)
      setIsOpen(false)
    } catch {
      // Error handled by mutation
    }
  }

  // Flatten folders with depth for indentation
  const flatFolders: Array<{ id: number; name: string; color: string; depth: number; document_count: number }> = []
  if (folders) {
    for (const folder of folders) {
      const depth = folder.breadcrumbs.length
      flatFolders.push({
        id: folder.id,
        name: folder.name,
        color: folder.color,
        depth,
        document_count: folder.document_count,
      })
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className={`w-full flex items-center gap-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 hover:border-neutral-300 dark:hover:border-neutral-600 transition-colors ${
          compact ? 'px-1.5 py-1 text-[10px]' : 'px-2 py-1.5 text-xs'
        }`}
      >
        <FolderIcon size={compact ? 10 : 12} className="shrink-0 text-neutral-400" />
        <span className="flex-1 text-left truncate">{displayName}</span>
        <ChevronDown size={10} className="shrink-0 text-neutral-400" />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1 w-full bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-50 py-1 max-h-[180px] overflow-y-auto">
          {/* No folder option */}
          <button
            onClick={() => {
              onSelect(null)
              setIsOpen(false)
            }}
            className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-left hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors text-neutral-500 dark:text-neutral-400"
          >
            <span className="flex-1">{t('organize.noFolder')}</span>
            {selectedFolderId == null && <Check size={10} className="text-primary-500" />}
          </button>

          {/* Folder list */}
          {flatFolders.map((folder) => (
            <button
              key={folder.id}
              onClick={() => {
                onSelect(folder.id)
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-left hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
              style={{ paddingLeft: `${8 + folder.depth * 12}px` }}
            >
              <span
                className="shrink-0 w-2 h-2 rounded-full"
                style={{ backgroundColor: folder.color || '#6B7280' }}
              />
              <span className="flex-1 truncate text-neutral-900 dark:text-neutral-100">
                {folder.name}
              </span>
              <span className="text-[10px] text-neutral-400">{folder.document_count}</span>
              {folder.id === selectedFolderId && <Check size={10} className="text-primary-500" />}
            </button>
          ))}

          {/* Create new folder */}
          {isCreating ? (
            <div className="flex items-center gap-1 px-2 py-1.5 border-t border-neutral-100 dark:border-neutral-700">
              <input
                ref={inputRef}
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreate()
                  if (e.key === 'Escape') {
                    setIsCreating(false)
                    setNewFolderName('')
                  }
                }}
                placeholder={t('organize.folderNamePlaceholder')}
                className="flex-1 min-w-0 px-1.5 py-1 text-xs rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
              />
              <button
                onClick={handleCreate}
                disabled={!newFolderName.trim() || createFolder.isPending}
                className="p-1 rounded text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 disabled:opacity-50"
              >
                <Check size={12} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsCreating(true)}
              className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-primary-600 dark:text-primary-400 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors border-t border-neutral-100 dark:border-neutral-700"
            >
              <Plus size={12} />
              {t('organize.newFolder')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
